import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X, PlusCircle, Send } from 'lucide-react';
import { workforceRequestApi } from '../../api/workforce-request';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { ReturnAction } from '../../components/ReturnAction';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { WORKFORCE_REQUEST_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const STATUS_LABELS: Record<string, string> = { draft: 'Draft', submitted: 'Pending Approval', returned: 'Returned for Redraft', approved: 'Approved', rejected: 'Rejected' };
const REQUEST_TYPE_LABELS: Record<string, string> = { new_position: 'New Position', replacement: 'Replacement' };
const EMPLOYMENT_TYPE_LABELS: Record<string, string> = { full_time: 'Full Time', part_time: 'Part Time', consultant: 'Consultant', intern: 'Intern', volunteer: 'Volunteer' };

export function WorkforceRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('workforce_requests.manage');

  const requestQuery = useQuery({ queryKey: ['workforce-request', requestId], queryFn: () => workforceRequestApi.get(requestId) });
  const docsQuery = useQuery({ queryKey: ['workforce-request-documents', requestId], queryFn: () => genericDocumentApi.listFor('workforce_request', requestId) });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['workforce-request', requestId] });

  const submitMutation = useMutation({
    mutationFn: () => workforceRequestApi.submit(requestId),
    onSuccess: () => { showSuccess('Submitted for approval.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit.'),
  });

  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => workforceRequestApi.decide(requestId, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  if (!requestQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const r = requestQuery.data.data;

  return (
    <div className="page">
      <Link to="/hr/workforce-requests">&larr; Back to Workforce Requests</Link>
      <RecordHeader
        title={r.request_reference}
        entityType="workforce_request"
        entityId={requestId}
        fields={[
          { label: 'Request type', value: REQUEST_TYPE_LABELS[r.request_type] ?? r.request_type },
          { label: 'Position', value: r.position_title ?? '-' },
          { label: 'Department', value: r.department_name ?? '-' },
          { label: 'Number of positions', value: String(r.number_of_positions) },
          { label: 'Grade', value: r.grade ?? '-' },
          { label: 'Employment type', value: r.employment_type ? EMPLOYMENT_TYPE_LABELS[r.employment_type] ?? r.employment_type : '-' },
          { label: 'Proposed start date', value: r.proposed_start_date ? formatDate(r.proposed_start_date) : '-' },
          { label: 'Requested by', value: r.requested_by_name ?? '-' },
        ]}
        actions={
          <div className="icon-action-group">
            {r.status === 'draft' && <button className="btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending}>{submitMutation.isPending ? 'Submitting…' : <><Send size={16} className="inline-icon" /> Submit</>}</button>}
            {r.status === 'submitted' && (
              <>
                <button className="btn-primary" onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}><Check size={16} className="inline-icon" /> Approve</button>
                <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}><X size={16} className="inline-icon" /> Reject</button>
                <ReturnAction onReturn={(comment) => workforceRequestApi.return(requestId, comment)} queryKeysToInvalidate={[['workforce-request', requestId]]} />
              </>
            )}
            {r.status === 'approved' && (
              <Link className="btn-primary" to={`/hr/vacancies?fromRequest=${r.id}`}><PlusCircle size={16} className="inline-icon" /> Create Vacancy</Link>
            )}
          </div>
        }
        readOnly={!canManage}
      />
      <p className="muted">Status: <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /></p>
      {r.status === 'returned' && <ReturnedBanner returnComment={r.return_comment} resubmit={() => workforceRequestApi.resubmit(requestId)} queryKeysToInvalidate={[['workforce-request', requestId]]} />}

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  <h3>Reason / Justification</h3>
                  <p>{r.reason}</p>
                </section>
                <CommentsThread entityType="workforce_request" entityId={requestId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('workforce_request', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('workforce_request', requestId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('workforce_request', documentId)}
                queryKeyToInvalidate={['workforce-request-documents', requestId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="workforce_request" entityId={requestId} actionStageMap={WORKFORCE_REQUEST_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
